/** @module ThemeToggle */

/** Class representing theme toggle functionality */
export default class ThemeToggle {
  /** Initialize the theme toggle functionality */
  constructor() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /** Initialize the component */
  init() {
    this.toggleBtns = document.querySelectorAll('.theme-toggle');
    this.switches = document.querySelectorAll('.switch');
    this.themeSwitches = document.querySelectorAll('.theme-switch');

    if (!this.toggleBtns.length) {
      return;
    }

    this.initTheme();
    this.bindEvents();
  }

  /** Initialize theme based on saved preference */
  initTheme() {
    // Check if body has light__mode-enable class - force light mode if present
    // but don't save to localStorage so it only affects this page
    if (
      document.body &&
      document.body.classList.contains('light__mode-enable')
    ) {
      this.setTheme('light', false); // false = don't save to localStorage
      return;
    }

    const savedTheme = localStorage.getItem('theme');
    const initialTheme = savedTheme || 'dark';
    this.setTheme(initialTheme);
  }

  /** Bind event listeners for theme toggling */
  bindEvents() {
    // Bind events to all toggle buttons
    this.toggleBtns.forEach((toggleBtn) => {
      // Listen for change event on the checkbox
      toggleBtn.addEventListener('change', () => this.handleThemeToggle());

      // Add keyboard support for Enter key
      toggleBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          ThemeToggle.toggleThemeState(toggleBtn);
          this.handleThemeToggle();
        }
      });
    });

    // Bind events to all switch elements
    this.switches.forEach((switchEl) => {
      // Also listen for click on the switch element for better mobile support
      switchEl.addEventListener('click', (e) => {
        // Prevent double triggering with checkbox
        if (e.target === switchEl) {
          e.preventDefault();
          // Find the associated toggle button and update it
          const associatedToggle = this.findAssociatedToggle(switchEl);
          if (associatedToggle) {
            ThemeToggle.toggleThemeState(associatedToggle);
            this.handleThemeToggle();
          }
        }
      });

      // Add keyboard support for the switch element as well
      switchEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const associatedToggle = this.findAssociatedToggle(switchEl);
          if (associatedToggle) {
            ThemeToggle.toggleThemeState(associatedToggle);
            this.handleThemeToggle();
          }
        }
      });
    });
  }

  /** Handle manual theme toggle */
  handleThemeToggle() {
    const currentTheme =
      document.documentElement.getAttribute('data-theme') || 'dark';
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }

  /**
   * Find the associated toggle button for a switch element
   * @param {HTMLElement} switchEl - The switch element
   * @returns {HTMLElement|null} The associated toggle button or null
   */
  findAssociatedToggle(switchEl) {
    // Look for a toggle button within the same container or nearby
    const container =
      switchEl.closest('.theme-switch') || switchEl.parentElement;
    if (container) {
      return container.querySelector('.theme-toggle');
    }
    // Fallback to the first toggle button if no association found
    return this.toggleBtns[0] || null;
  }

  /**
   * Toggle the state of a toggle button
   * @param {HTMLElement} toggleBtn - The toggle button element
   */
  static toggleThemeState(toggleBtn) {
    // eslint-disable-next-line no-param-reassign
    toggleBtn.checked = !toggleBtn.checked;
  }

  /**
   * Set the state of a toggle button
   * @param {HTMLElement} toggleBtn - The toggle button element
   * @param {boolean} checked - Whether the toggle should be checked
   */
  static setToggleState(toggleBtn, checked) {
    // eslint-disable-next-line no-param-reassign
    toggleBtn.checked = checked;
  }

  /**
   * Set the theme and update UI elements
   * @param {string} theme - The theme to set ('dark' or 'light')
   * @param {boolean} saveToStorage - Whether to save the theme to localStorage (default: true)
   */
  setTheme(theme, saveToStorage = true) {
    if (!theme || !['dark', 'light', 'light-blue'].includes(theme)) {
      return;
    }

    if (document.documentElement) {
      document.documentElement.setAttribute('data-theme', theme);
    }

    // Only save to localStorage if saveToStorage is true
    // This allows page-specific theme overrides without affecting other pages
    if (saveToStorage) {
      try {
        localStorage.setItem('theme', theme);
      } catch (e) {
        // Handle case where localStorage is not available
      }
    }

    // Update all checkbox states
    this.toggleBtns.forEach((toggleBtn) => {
      ThemeToggle.setToggleState(toggleBtn, theme === 'light');
    });

    // Update all switch ARIA attributes
    this.switches.forEach((switchEl) => {
      switchEl.setAttribute('aria-checked', theme === 'light');
    });

    // Add theme class to all theme switch containers
    this.themeSwitches.forEach((themeSwitch) => {
      themeSwitch.classList.toggle('is-dark', theme === 'light');
    });
  }
}

// Initialize when loaded as a module
(() => new ThemeToggle())();
